import fetch from 'isomorphic-fetch';
import * as Sentry from '@sentry/browser';
import {
  Keycloak,
  CookieStorage,
  Tokens,
  InitState as KeycloakInitState,
} from '@unitedtraders/keycloak';
import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { from, ApolloLink, Observable } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { AppConfig } from 'types/config';
import { i18n as I18n } from 'i18next';
import { SagaContext } from 'shared/types/common';
import introspectionQueryResultData from '../fragmentTypes.json';
import { getCookieDomain } from './domain';

interface AppState {
  apollo?: any;
  keycloak?: KeycloakInitState;
  asap?: boolean;
  i18nInstance: I18n;
  host?: string;
}

const tokensToHeaders = (tokens: Tokens) =>
  tokens && {
    headers: {
      Authorization: `Bearer ${tokens.accessToken}`,
    },
  };

const blackHoleLink = new ApolloLink(
  () => new Observable((observable) => () => observable.complete())
);

function getTabsCount() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const tabs = JSON.parse(window.localStorage.getItem('tabsOpened') || '{}');
    return Object.keys(tabs).length;
  } catch (e) {
    return;
  }
}

export function initServices(config: AppConfig, state: AppState) {
  const storage = CookieStorage.isSupported()
    ? new CookieStorage({
        refreshTokenName: 'refresh_token',
        accessTokenName: 'access_token',
        ...config.tokenStorage,
        domain: getCookieDomain(state.host),
      })
    : undefined;

  const keycloakService = new Keycloak(config.keycloak, state.keycloak, storage);

  keycloakService.on('error', ({ message, attributes }) => {
    Sentry.withScope((scope) => {
      scope.setExtra('error', {
        tabsCount: getTabsCount(),
        isSSR: __ISSERVER__,
      });
      Sentry.captureMessage(
        `[Keycloak]: ${message} (${attributes?.reason})`,
        Sentry.Severity.Error
      );
    });
  });

  const authMiddleware = setContext(() =>
    keycloakService
      .getTokens()
      .then(tokensToHeaders)
      .catch((e) => console.log(e.message))
  );

  const logMiddleware = new ApolloLink((operation, forward) => {
    operation.query.definitions.map(
      (definition) =>
        definition.kind === 'OperationDefinition' &&
        definition.name &&
        console.log(`Gql operation:`, definition.name.value)
    );

    return forward(operation);
  });

  const localeMiddleware = setContext((operation, prevContext) => ({
    ...prevContext,
    headers: {
      ...(prevContext.headers || {}),
      'Accept-Language': state.i18nInstance.language,
    },
  }));

  const httpLink = createHttpLink({
    uri: config.utcomGateway.url,
    credentials: 'same-origin',
    fetch,
  });

  const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData });

  const errorLink = onError(({ graphQLErrors, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.map((error) => {
        console.error(operation.operationName, error.message);
        Sentry.withScope((scope) => {
          scope.setExtra('error', error);
          Sentry.captureMessage(`[${operation.operationName}] ${error.message}`);
        });
      });
    }
  });

  const cache = new InMemoryCache({ fragmentMatcher });

  const apolloClient = new ApolloClient({
    ssrMode: __ISSERVER__,
    link: from([
      logMiddleware,
      errorLink,
      authMiddleware,
      localeMiddleware,
      state.asap ? blackHoleLink : httpLink,
    ]),
    cache: cache.restore(state.apollo),
    connectToDevTools: true,
  });

  return {
    [SagaContext.KeycloakService]: keycloakService,
    [SagaContext.ApolloClient]: apolloClient,
  };
}

export type Services = ReturnType<typeof initServices>;
